import { AHButton, AHText, Box } from "arkh";
import { useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { StoreAdminNavigationScreenProps } from "../../StoreAdmin/StoreAdminNavigation";
import { InfoBox } from "./InfoBox";
import { AddScreenInfoDismissMutation } from "./__generated__/AddScreenInfoDismissMutation.graphql";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { AddScreenInfoQuery } from "./__generated__/AddScreenInfoQuery.graphql";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { StorecastScreenModeSelector } from "../StorecastScreenAdminModeSelector";

export const AddScreenInfo = (
  props: StoreAdminNavigationScreenProps<"AddScreenInfo">
) => {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerAddScreenInfo {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
};

const InnerAddScreenInfo = ({
  fetchKey,
  ...props
}: StoreAdminNavigationScreenProps<"AddScreenInfo"> & { fetchKey: string }) => {
  const screenId = props.route.params.screenId;

  const intlTokens = useIntlTokens();

  const data = useLazyLoadQuery<AddScreenInfoQuery>(
    graphql`
      query AddScreenInfoQuery($screenId: ID) {
        storecast {
          screen(id: $screenId) {
            ...InfoBox_StorecastScreen
            ...StorecastScreenAdminModeSelector_StorecastScreen
            inputMode
          }
        }
      }
    `,
    {
      screenId,
    },
    {
      fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [dismissInfo] = useMutation<AddScreenInfoDismissMutation>(graphql`
    mutation AddScreenInfoDismissMutation($screenId: ID!) {
      storecast {
        dismissInfo(screenId: $screenId) {
          id
          name
        }
      }
    }
  `);

  const onDismiss = useCallback(() => {
    dismissInfo({
      variables: { screenId: props.route.params.screenId },
      onCompleted: (data) => {
        if (props.route.params.fromStorecast) {
          props.navigation.replace("AddScreenSuccess", {
            screenName: data.storecast.dismissInfo.name,
            dashboardId: props.route.params.fromStorecast.id,
            dashboardName: props.route.params.fromStorecast.name,
            fromStorecast: props.route.params.fromStorecast,
          });
        } else {
          props.navigation.replace("AddScreenAssignDashboardPrompt", {
            screenId: data.storecast.dismissInfo.id,
            screenName: data.storecast.dismissInfo.name ?? "",
          });
        }
      },
    });
  }, [
    props.navigation,
    props.route.params.screenId,
    props.route.params.fromStorecast,
    dismissInfo,
  ]);
  const safeAreaInsets = useSafeAreaInsets();

  return (
    <Box
      spaceHorizontal="medium"
      spaceTop="large"
      style={{ flex: 1 }}
      insets={{
        bottom: safeAreaInsets.bottom,
      }}
      spaceBottom="medium"
    >
      <ScrollView>
        <AHText type="title-big" textAlign="center">
          <FormattedMessage
            description="AddScreenInfo title"
            defaultMessage="Screen status"
          />
        </AHText>
        <Box spaceTop="large" />
        <AHText type="list-item-title" spaceHorizontal="medium">
          <FormattedMessage
            description="AddScreenInfo subtitle"
            defaultMessage="Check the screen in order to make sure that the settings are correct."
          />
        </AHText>
        <Box spaceTop="large" />
        <InfoBox screen={data.storecast.screen} />

        <StorecastScreenModeSelector screen={data.storecast.screen} />
      </ScrollView>
      <Box spaceTop="medium" color="background">
        <AHButton
          title={intlTokens.Next}
          themeType="primary"
          onPress={onDismiss}
        />
      </Box>
    </Box>
  );
};
